// frontend/src/pages/Payment/RenewSubscriptionPage.js
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiCalendar, FiCheck, FiCreditCard, FiLock, FiShield, FiStar, FiClipboard } from 'react-icons/fi';
import {
  createSubscriptionOrder, getSubscriptionPlans,
  verifySubscriptionRenewal, offlineRenewal,
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import useRazorpay from '../../hooks/useRazorpay';
import styles from './PaymentPage.module.css';

const formatPrice = (price) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);

const RenewSubscriptionPage = () => {
  const navigate = useNavigate();
  const {
    user, activeDerasarId, activeDerasar,
    activeDerasarHasPendingRenewal, isActiveDerasarTrial, refreshUser,
  } = useAuth();
  const { openCheckout } = useRazorpay();

  const [plans, setPlans]                   = useState([]);
  const [offlineEnabled, setOfflineEnabled] = useState(false);
  const [selectedPlan, setSelectedPlan]     = useState(null);
  const [plansLoading, setPlansLoading]     = useState(true);
  const [paying, setPaying]                 = useState(false);
  const [offlinePaying, setOfflinePaying]   = useState(false);

  useEffect(() => {
    if (!activeDerasarId) { navigate('/dashboard'); return; }
    if (activeDerasarHasPendingRenewal) {
      toast.info('Renewal payment is already submitted and awaiting approval.');
      navigate('/derasar/my');
      return;
    }
    if (isActiveDerasarTrial) {
      toast.info('Trial accounts cannot be renewed.');
      navigate('/derasar/my');
      return;
    }
    getSubscriptionPlans(user?.email)
      .then((res) => {
        setPlans(res.plans);
        setOfflineEnabled(res.offlinePaymentEnabled || false);
        if (res.plans.length > 0) setSelectedPlan(res.plans[0]);
      })
      .catch(() => toast.error('Failed to load subscription plans'))
      .finally(() => setPlansLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDerasarHasPendingRenewal, isActiveDerasarTrial, activeDerasarId]);

  const handlePay = async () => {
    if (!selectedPlan || !activeDerasarId) return;
    setPaying(true);
    try {
      const orderRes = await createSubscriptionOrder({
        years: selectedPlan.years,
        planItemId: selectedPlan.id,
        purpose: 'renewal',
        email: user?.email,
      });
      const { orderId, amount, currency, key } = orderRes.data || orderRes;

      const payment = await openCheckout({
        key, amount, currency,
        name: 'JainamSevaSetu',
        description: `${selectedPlan.label} Renewal`,
        order_id: orderId,
        prefill: { name: user?.name, email: user?.email, contact: user?.phone },
        notes: { derasar_name: activeDerasar?.derasarId?.name, derasar_id: String(activeDerasarId) },
        theme: { color: '#e0a106' },
      });

      await verifySubscriptionRenewal({
        derasarId: activeDerasarId,
        subscriptionYears: selectedPlan.years,
        planItemId: selectedPlan.id,
        razorpay_payment_id: payment.razorpay_payment_id,
        razorpay_order_id:   payment.razorpay_order_id,
        razorpay_signature:  payment.razorpay_signature,
      });

      await refreshUser();
      toast.success('Renewal payment successful. Awaiting super admin approval.');
      navigate('/derasar/my');
    } catch (err) {
      if (err.message === 'cancelled') {
        toast.info('Payment cancelled');
      } else {
        toast.error(err.response?.data?.message || err.message || 'Renewal payment failed. Please try again.');
      }
    } finally {
      setPaying(false);
    }
  };

  const handleOfflinePay = async () => {
    if (!selectedPlan || !activeDerasarId) return;
    setOfflinePaying(true);
    try {
      await offlineRenewal({
        derasarId: activeDerasarId,
        subscriptionYears: selectedPlan.years,
        planItemId: selectedPlan.id,
      });
      await refreshUser();
      toast.success('Offline renewal submitted. Awaiting payment confirmation and super admin approval.');
      navigate('/derasar/my');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Offline renewal failed.');
    } finally {
      setOfflinePaying(false);
    }
  };

  if (!activeDerasarId) return null;

  return (
    <div className={styles.page}>
      <div className={styles.leftPanel}>
        <div className={styles.brandRow}>
          <img src="/icon.png" alt="Jainam Seva Setu" className={styles.brandIcon} />
          <span className={styles.brandName}>JainamSevaSetu</span>
        </div>

        <div className={styles.summaryBox}>
          <div className={styles.summaryTitle}>Renewal Summary</div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Derasar Name</span>
            <span className={styles.summaryValue}>{activeDerasar?.derasarId?.name}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Location</span>
            <span className={styles.summaryValue}>{activeDerasar?.derasarId?.city}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Admin</span>
            <span className={styles.summaryValue}>{user?.name}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Email</span>
            <span className={styles.summaryValue}>{user?.email}</span>
          </div>
        </div>

        {selectedPlan && (
          <div className={styles.orderBox}>
            <div className={styles.orderTitle}>Order Details</div>
            <div className={styles.orderRow}>
              <span>Renewal ({selectedPlan.label})</span>
              <span>{formatPrice(selectedPlan.price)}</span>
            </div>
            <div className={styles.orderDivider} />
            <div className={`${styles.orderRow} ${styles.orderTotal}`}>
              <span>Total</span>
              <span>{formatPrice(selectedPlan.price)}</span>
            </div>
          </div>
        )}

        <div className={styles.secureNote}>
          <FiShield size={12} /> Payments secured by Razorpay
        </div>
      </div>

      <div className={styles.rightPanel}>
        <div className={styles.formContainer}>
          <h2 className={styles.formTitle}>Renew Subscription</h2>
          <p className={styles.formSubtitle}>Choose a plan and complete payment to send renewal for super admin approval</p>

          {plansLoading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : (
            <div className={styles.plansGrid}>
              {plans.map((plan) => (
                <div
                  key={plan.id || plan.years}
                  className={`${styles.planCard} ${selectedPlan?.id === plan.id ? styles.planSelected : ''} ${plan.isSpecialOffer ? styles.planSpecial : ''}`}
                  onClick={() => setSelectedPlan(plan)}
                >
                  {plan.isSpecialOffer && (
                    <div className={styles.specialBadge}><FiStar size={10} /> Special Offer</div>
                  )}
                  <div className={styles.planYears}>{plan.label}</div>
                  <div className={styles.planPrice}>{formatPrice(plan.price)}</div>
                  <div className={styles.planPricePerYear}>{formatPrice(Math.round(plan.price / plan.years))} / year</div>
                  <div className={styles.planCheck}>
                    {selectedPlan?.id === plan.id && <FiCheck size={14} />}
                  </div>
                </div>
              ))}
            </div>
          )}

          {selectedPlan && (
            <div className={styles.selectedSummary}>
              <FiCalendar size={14} style={{ marginRight: 6 }} />
              Selected: <strong>{selectedPlan.label}</strong> - {formatPrice(selectedPlan.price)}
            </div>
          )}

          <button
            className="btn btn-primary btn-block"
            style={{ marginTop: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            onClick={handlePay}
            disabled={!selectedPlan || plansLoading || paying || offlinePaying}
            type="button"
          >
            <FiCreditCard size={16} />
            {paying ? 'Opening Razorpay...' : `Pay ${selectedPlan ? formatPrice(selectedPlan.price) : ''} via Razorpay`}
          </button>

          {offlineEnabled && (
            <button
              className="btn btn-secondary btn-block"
              style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              onClick={handleOfflinePay}
              disabled={!selectedPlan || plansLoading || paying || offlinePaying}
              type="button"
            >
              <FiClipboard size={16} />
              {offlinePaying ? 'Submitting…' : 'Pay via Offline'}
            </button>
          )}

          <div className={styles.razorpayNote}>
            <FiLock size={12} />
            {offlineEnabled
              ? 'Online: Razorpay secure checkout. Offline: super admin will confirm your payment manually.'
              : 'After payment, your derasar remains read-only until super admin approves the renewal.'}
          </div>

          <button
            className="btn btn-ghost btn-block"
            style={{ marginTop: 8 }}
            onClick={() => navigate('/derasar/my')}
            disabled={paying || offlinePaying}
            type="button"
          >
            Back to My Derasar
          </button>
        </div>
      </div>
    </div>
  );
};

export default RenewSubscriptionPage;
